import { AccountMeta, OktaMetaData } from 'api/account';
import { AuthenticationType } from 'api/models';
import {
  LoginPolicyType,
  SamlConfig,
  UpdateSsoPayload,
} from 'api/security/idp';
import { useAccountMetaQuery } from 'queries';

export interface SetupFormField {
  audienceUrl: string;
  ssoUrl: string;
  certificate: string;
}

export const getChangedPayload = (
  oldCofig: SamlConfig,
  formData: SetupFormField,
): UpdateSsoPayload => {
  const payload = {
    idpId: oldCofig.idpId,
    enabled: oldCofig.enabled,
    saml: {},
  };

  Object.entries(formData).forEach(([key, value]) => {
    if (formData[key] !== oldCofig?.idpMeta?.[key]) {
      payload.saml[key] = value;
    }
  });

  return payload;
};

export enum LoginPolicyDisplayType {
  Optional = 'Optional',
  Only = 'Only',
}

export const getLoginPolicyDisplayValue = (
  accountMeta?: AccountMeta,
  loginPolicy?: Array<AuthenticationType>,
): LoginPolicyDisplayType | undefined => {
  if (!accountMeta || !loginPolicy) {
    return undefined;
  }
  const { companyId } = accountMeta.company;

  const idp = loginPolicy.find(
    (policy) =>
      policy.companyId === companyId && policy.authType === LoginPolicyType.Idp,
  );

  return idp?.mandatory
    ? LoginPolicyDisplayType.Only
    : LoginPolicyDisplayType.Optional;
};

const getSaml = (samlConfigs?: SamlConfig[]) =>
  samlConfigs?.find((config) => config.idpId);

export const getLoginPolicyPayload = (
  displayValue: LoginPolicyDisplayType,
  accountMeta?: AccountMeta,
  oldLoginPolicy?: Array<AuthenticationType>,
  samlConfigs?: SamlConfig[],
): Array<AuthenticationType> | undefined => {
  if (!accountMeta || !oldLoginPolicy) {
    return undefined;
  }
  const { companyId } = accountMeta.company;

  const newPolicy = oldLoginPolicy.map((policy) => {
    if (policy.companyId !== companyId) {
      return policy;
    }
    if (displayValue === LoginPolicyDisplayType.Only) {
      if (policy.authType === LoginPolicyType.Idp) {
        return {
          ...policy,
          mandatory: true,
        };
      }
    }

    return {
      ...policy,
      mandatory: false,
    };
  });

  const predicateByOurIdp = (policy: AuthenticationType) =>
    policy.companyId === companyId && policy.authType === LoginPolicyType.Idp;

  if (newPolicy.find(predicateByOurIdp)) {
    return newPolicy;
  }

  const samlConfig = getSaml(samlConfigs);

  if (!samlConfig) {
    return newPolicy;
  }

  return [
    ...newPolicy,
    {
      authType: LoginPolicyType.Idp,
      companyId,
      enabled: true,
      default: false,
      idpConfigId: samlConfig.accountIdpConfigId,
      mandatory: displayValue === LoginPolicyDisplayType.Only,
    },
  ];
};

export const getEnabledIdp = (oktaMetaData?: OktaMetaData) => {
  return oktaMetaData?.idps?.find((idp) => idp.enabled);
};

export const useIsSamlMandatory = (): boolean => {
  const { data } = useAccountMetaQuery();

  if (!data) {
    return false;
  }

  return (
    data.company?.authentications?.find(
      (auth) => auth.authType === LoginPolicyType.Idp,
    )?.mandatory || false
  );
};
